import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useKintoneEnvironment } from "@/features/app-bindings/hooks/useKintoneEnvironment";
import type { KintoneEnvironmentCurrent } from "@/api/kintone-environment";

type Commercial = KintoneEnvironmentCurrent["commercial"];

/** Days left at which the trial end is emphasized everywhere (§2.3). */
export const TRIAL_ATTENTION_DAYS = 7;

export type PlanStatusTone = "info" | "attention" | "stopped";

/** What the organization should hear about its plan now, or null (§2.3). */
export function planStatusOf(commercial: Commercial): {
  tone: PlanStatusTone;
  title: string;
  body: string;
  memberBody: string;
  action: string;
} | null {
  // The environment payload does not say whether the contract pays by card or
  // by invoice, so the copy fits both; the service subscription page shows the
  // card- or invoice-specific steps (D-7).
  if (commercial.commercialState === "payment_required") {
    return {
      tone: "stopped",
      title: "お支払いを確認できないため、サービスを停止しています",
      body: "お支払い方法の更新か、未払いの請求書のお支払いを確認できると、すぐに使えるようになります。",
      memberBody: "再開するには、組織の管理者に連絡してください。",
      action: "お支払いを確認",
    };
  }
  if (commercial.commercialState === "expired") {
    return {
      tone: "stopped",
      title: "お試し期間が終了したため、サービスを停止しています",
      body: "有料プランを申し込むと、登録済みのアプリと設定のまま、すぐに使えるようになります。",
      memberBody: "再開するには、組織の管理者に連絡してください。",
      action: "有料プランを申し込む",
    };
  }
  // In beta the trial has no end date, so nothing is shown.
  if (commercial.commercialState !== "trial_active" || commercial.releasePhase !== "formal" ||
      commercial.trialDaysRemaining === null) return null;
  const days = commercial.trialDaysRemaining;
  return {
    tone: days <= TRIAL_ATTENTION_DAYS ? "attention" : "info",
    title: days === 0 ? "お試し期間は今日で終了します" : `お試し期間は残り${days}日です`,
    body: "期間が終わると、kintone のプラグインから添付ファイルを保存・表示できなくなります。引き続きお使いいただく場合は、有料プランをお申し込みください。",
    memberBody: "期間が終わると、kintone のプラグインから添付ファイルを保存・表示できなくなります。続けて使う場合は、組織の管理者に連絡してください。",
    action: "有料プランを見る",
  };
}

const TONE_CLASS: Record<PlanStatusTone, string> = {
  info: "border-sky-200 bg-sky-50 text-sky-950",
  attention: "border-amber-300 bg-amber-50 text-amber-950",
  stopped: "border-red-300 bg-red-50 text-red-950",
};

/**
 * Trial end and stopped-service banner (C1). The layout shows it when action
 * is due; the dashboard shows only the quiet remaining-days notice, so the
 * two never repeat each other.
 */
export function PlanStatusBanner({ placement = "layout", className = "" }: { placement?: "layout" | "dashboard"; className?: string }) {
  const { user } = useAuth();
  const { environment } = useKintoneEnvironment();
  if (!environment) return null;
  const status = planStatusOf(environment.commercial);
  if (!status || (status.tone === "info") !== (placement === "dashboard")) return null;
  const isAdmin = user?.currentMembership?.role === "customer_admin";
  return (
    <div role={status.tone === "info" ? "status" : "alert"} className={`flex flex-col gap-2 rounded-md border px-4 py-3 text-sm md:flex-row md:items-center md:justify-between ${TONE_CLASS[status.tone]} ${className}`}>
      <div>
        <p className="font-semibold">{status.title}</p>
        <p className="mt-0.5 opacity-90">{isAdmin ? status.body : status.memberBody}</p>
      </div>
      {isAdmin ? (
        <Link to="/console/service-subscription" className="inline-flex shrink-0 items-center rounded-md bg-background px-3 py-1.5 font-medium shadow-sm ring-1 ring-inset ring-current/20 hover:bg-background/80">
          {status.action}
        </Link>
      ) : null}
    </div>
  );
}
